/**
 * PageExtractor - Regex-based extraction of tableClms, API endpoints, form fields from existing .js files.
 * Used by BatchMigration admin page in Phase 9.
 */

/**
 * Extract widget configuration from an existing page component source code
 * @param {string} sourceCode - The full source code of a page component
 * @param {string} pageName - The page/table name (e.g., "Testtable")
 * @returns {Object} Extracted configuration
 */
export function extractPageConfig(sourceCode, pageName) {
  const config = {
    pageName,
    tableName: pageName.toLowerCase(),
    columns: [],
    formFields: [],
    apiEndpoints: {},
    idField: "",
    formTitle: "",
  };

  // Extract ID field: {tablename}_Id
  const idMatch = sourceCode.match(/(\w+_Id)/);
  if (idMatch) {
    config.idField = idMatch[1];
  }

  // Extract table columns from tableClms
  const colRegex = /\{name:\s*"([^"]+)",\s*selector:\s*\(row(?:,\s*i)?\)\s*=>\s*row\.(\w+)/g;
  let colMatch;
  while ((colMatch = colRegex.exec(sourceCode)) !== null) {
    config.columns.push({
      header: colMatch[1],
      field: colMatch[2],
      sortable: true,
      type: "text",
    });
  }

  // Also extract columns with different formatting
  const colRegex2 = /\{\s*name:\s*"([^"]+)",\s*\n?\s*selector:\s*\(row(?:,\s*i)?\)\s*=>\s*row\.(\w+)/g;
  while ((colMatch = colRegex2.exec(sourceCode)) !== null) {
    const exists = config.columns.find((c) => c.field === colMatch[2]);
    if (!exists) {
      config.columns.push({
        header: colMatch[1],
        field: colMatch[2],
        sortable: true,
        type: "text",
      });
    }
  }

  // Extract API endpoints
  const fetchMatch = sourceCode.match(/configData\.api_url\s*\+\s*['"]([^'"]+\/list)['"]/);
  if (fetchMatch) config.apiEndpoints.list = fetchMatch[1];

  const addMatch = sourceCode.match(/configData\.api_url\s*\+\s*['"]([^'"]+\/add)['"]/);
  if (addMatch) config.apiEndpoints.add = addMatch[1];

  const updateMatch = sourceCode.match(/configData\.api_url\s*\+\s*['"]([^'"]+\/update)['"]/);
  if (updateMatch) config.apiEndpoints.update = updateMatch[1];

  const deleteMatch = sourceCode.match(/configData\.api_url\s*\+\s*['"]([^'"]+\/delete)['"]/);
  if (deleteMatch) config.apiEndpoints.delete = deleteMatch[1];

  // Extract form fields from formData.append calls (excluding sId, uId, and Id fields)
  const formDataRegex = /formData\.append\(['"](\w+)['"]\s*,\s*this\.state\.(\w+)\)/g;
  let fdMatch;
  const seenFields = new Set();
  while ((fdMatch = formDataRegex.exec(sourceCode)) !== null) {
    const fieldName = fdMatch[1];
    if (!seenFields.has(fieldName) && !["sId", "uId"].includes(fieldName) && !fieldName.endsWith("_Id")) {
      seenFields.add(fieldName);
      config.formFields.push({
        fieldName,
        displayName: fieldName,
        inputType: "text",
        required: false,
      });
    }
  }

  // Extract form field labels from Form.Label
  const labelRegex = /Form\.Label[^>]*>([^<]+)<\/Form\.Label>[\s\S]*?name="(\w+)"/g;
  let labelMatch;
  while ((labelMatch = labelRegex.exec(sourceCode)) !== null) {
    const field = config.formFields.find((f) => f.fieldName === labelMatch[2]);
    if (field) {
      field.displayName = labelMatch[1].trim();
    }
  }

  // Extract form title
  const titleMatch = sourceCode.match(/Offcanvas\.Title>([^<]+)<\/Offcanvas\.Title>/);
  if (titleMatch) config.formTitle = titleMatch[1].trim();

  // Extract page heading
  const headingMatch = sourceCode.match(/<h3[^>]*>([^<]+)<\/h3>/);
  if (headingMatch) config.pageTitle = headingMatch[1].trim();

  return config;
}

/**
 * Generate widget instance config JSON from extracted config
 */
export function toWidgetInstanceConfig(extracted) {
  return {
    instanceName: extracted.pageName + "_table",
    widget_types_Id: 1, // data_table_rdt
    apiEndPoint: extracted.apiEndpoints.list || `C_${extracted.pageName}/list`,
    apiMethod: "POST",
    columnsConfig: JSON.stringify(extracted.columns),
    actionsConfig: JSON.stringify({
      canAdd: !!extracted.apiEndpoints.add,
      canEdit: !!extracted.apiEndpoints.update,
      canDelete: !!extracted.apiEndpoints.delete,
      addEndpoint: extracted.apiEndpoints.add || `C_${extracted.pageName}/add`,
      editEndpoint: extracted.apiEndpoints.update || `C_${extracted.pageName}/update`,
      deleteEndpoint: extracted.apiEndpoints.delete || `C_${extracted.pageName}/delete`,
      permissionPrefix: extracted.tableName,
    }),
    tableConfig: JSON.stringify({
      pagination: true,
      paginationPerPage: 100,
      searchable: true,
      idField: extracted.idField || extracted.tableName + "_Id",
    }),
    formConfig: JSON.stringify({
      fields: extracted.formFields,
    }),
    sourceTable: extracted.tableName,
  };
}

/**
 * Generate _NEW.js wrapper file content
 */
export function generateNewPageContent(pageName, pageRoute) {
  return `import React, { Component } from "react";
import WidgetPageRenderer from "../widgets/WidgetPageRenderer";

export default class ${pageName}_NEW extends Component {
  render() {
    return <WidgetPageRenderer pageRoute="${pageRoute}" />;
  }
}
`;
}

export default { extractPageConfig, toWidgetInstanceConfig, generateNewPageContent };
